import { IMPORTANT_MARK } from '@ergo/shared';
import type {
  AccountMovement,
  CreateLocationRequest,
  GeocodedPoint,
  Location,
  Patient,
} from '@ergo/shared';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorMessage } from '../api';
import { STATUS_LABELS, placesOf } from '../appointments';
import { AddressInput } from '../components/AddressInput';
import { formatSent } from '../cloture';
import { splitReleve } from '../compte';
import { PaymentForm, SoldeChip } from '../components/Compte';
import { MapCanvas } from '../components/MapCanvas';
import { useResource } from '../hooks';
import { type MapPoint, useEmailConfigured, useMapsKey } from '../maps';
import { formatEuros, formatSignedEuros } from '../money';
import {
  formatDate,
  formatDayMonth,
  formatDuration,
  formatShortDate,
  now,
  timeOf,
  today,
} from '../time';
import { useRefreshNavCounts } from '../unlogged';

/** Wide enough to cover every appointment the patient has or will have. */
const ALL_TIME = { from: '2000-01-01', to: '2100-12-31' };

export function PatientDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const patientId = Number(params.id);

  const patient = useResource(() => api.patients.get(patientId), [patientId]);
  const appointments = useResource(
    () => api.appointments.list({ ...ALL_TIME, patientId }),
    [patientId],
  );

  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const upcoming = (appointments.data ?? []).filter((item) => item.startsAt >= now()).slice(0, 12);
  const hasHistory = (appointments.data ?? []).length > 0;

  async function run(action: () => Promise<unknown>) {
    setPending(true);
    setError(null);
    try {
      await action();
      patient.reload();
      appointments.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  async function onDelete() {
    if (!window.confirm('Supprimer définitivement ce patient et ses lieux ?')) return;
    setPending(true);
    setError(null);
    try {
      const result = await api.patients.remove(patientId);
      if (result.deleted) {
        navigate('/patients', { replace: true });
        return;
      }
      // The patient turned out to have sessions: the API deactivated instead.
      patient.reload();
      setError('Ce patient a des séances : il a été désactivé plutôt que supprimé.');
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  if (patient.error) {
    return (
      <section className="page">
        <p className="error" role="alert">{patient.error}</p>
        <Link className="button" to="/patients">Retour aux patients</Link>
      </section>
    );
  }

  const data: Patient | null = patient.data;
  if (!data) return <p className="muted">Chargement…</p>;

  return (
    <section className="page">
      <header className="page__header">
        <div>
          <Link className="backlink" to="/patients">← Patients</Link>
          <h1 className="page__title">
            {data.name}
            {data.cabinet && <span className="badge">Cabinet</span>}
            {!data.active && <span className="badge badge--muted">Inactif</span>}
          </h1>
          {data.contact && <p className="page__lead">{data.contact}</p>}
          {billingLabel(data) && <p className="hint">Facturation : {billingLabel(data)}</p>}
          {data.email && (
            <p className="hint">
              {data.email} ·{' '}
              {data.emailConsent
                ? 'accepte les factures par email'
                : 'n’accepte pas les factures par email'}
            </p>
          )}
        </div>
        <div className="page__header-actions">
          <Link className="button" to={`/patients/${patientId}/modifier`}>
            Modifier
          </Link>
          <Link
            className="button button--primary"
            to={`/rdv/nouveau?patientId=${patientId}`}
            state={{ from: `/patients/${patientId}` }}
          >
            Nouvelle séance
          </Link>
        </div>
      </header>

      {error && <p className="error" role="alert">{error}</p>}

      {data.notes && (
        <div className="panel">
          <h2 className="panel__title">Notes</h2>
          <p className="notes">{data.notes}</p>
        </div>
      )}

      <ComptePanel patientId={patientId} />

      <LocationsPanel patient={data} pending={pending} onChanged={() => patient.reload()} />

      <div className="panel">
        <h2 className="panel__title">Séances à venir</h2>
        {appointments.loading && !appointments.data && <p className="muted">Chargement…</p>}
        {appointments.data && upcoming.length === 0 && (
          <p className="empty">Aucune séance prévue.</p>
        )}
        <ul className="list">
          {upcoming.map((item) => (
            <li key={item.id}>
              <Link
                className="list__row list__row--link"
                to={`/rdv/${item.id}`}
                state={{ from: `/patients/${patientId}` }}
              >
                <span className="list__lead">{formatDayMonth(item.startsAt.slice(0, 10))}</span>
                <span className={item.important ? 'is-important' : undefined}>
                  {item.important && `${IMPORTANT_MARK} `}
                  {/* Sur sa fiche, le patient va de soi : l'intitulé ouvre la ligne. */}
                  {item.title && `${item.title} · `}
                  {timeOf(item.startsAt)} · {formatDuration(item.plannedDurationMin)} ·{' '}
                  {item.locationLabel}
                </span>
                {item.status !== 'planned' && (
                  <span className="badge badge--muted">{STATUS_LABELS[item.status]}</span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="panel panel--danger">
        <h2 className="panel__title">Gestion du dossier</h2>
        <div className="form__actions">
          <button
            type="button"
            className="button"
            disabled={pending}
            onClick={() => void run(() => api.patients.update(patientId, { active: !data.active }))}
          >
            {data.active ? 'Désactiver' : 'Réactiver'}
          </button>
          {!hasHistory && appointments.data && (
            <button type="button" className="button button--danger" disabled={pending} onClick={() => void onDelete()}>
              Supprimer
            </button>
          )}
        </div>
        <p className="hint">
          {hasHistory
            ? 'Ce patient a des séances enregistrées : il ne peut qu’être désactivé, pour garder l’historique de facturation.'
            : 'Aucune séance enregistrée : ce patient peut encore être supprimé.'}
        </p>
      </div>
    </section>
  );
}

/** Le lieu dont l'adresse part sur ses factures ; rien tant qu'elle n'en a désigné aucun. */
function billingLabel(patient: Patient): string | null {
  const location = patient.locations.find((item) => item.id === patient.billingLocationId);
  return location?.label ?? null;
}

/**
 * Le compte : le solde, puis le relevé — factures et paiements mêlés, du plus récent
 * au plus ancien, avec le solde courant à chaque ligne. Une facture ne se supprime pas
 * (elle est immuable) ; un paiement, si — c'est son livre de comptes.
 */
function ComptePanel({ patientId }: { patientId: number }) {
  const account = useResource(() => api.patients.account(patientId), [patientId]);
  const refreshNav = useRefreshNavCounts();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const email = useEmailConfigured();

  const releve = account.data?.releve ?? [];
  const { open, history } = splitReleve(releve);
  const shown = historyOpen ? releve : open;

  /** Un mouvement au grand livre : le relevé bouge, et la pastille Patients avec. */
  function onMoved() {
    account.reload();
    refreshNav();
  }

  /** (Ré)envoyer une facture depuis la fiche : même envoi que dans le Rapport. */
  async function onSend(movement: AccountMovement) {
    const again = movement.sentAt !== null;
    if (again && !window.confirm(`Renvoyer la facture ${movement.label.split(' — ')[0]} ?`)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.notes.send(movement.id);
      setNotice(
        result.failed.length > 0
          ? `Envoyée à ${result.note.sentTo ?? ''} — échec : ${result.failed.join(', ')}`
          : `Facture ${again ? 'renvoyée' : 'envoyée'}.`,
      );
      onMoved();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  /** « Payé » : le montant exact de la facture entre au livre, daté d'aujourd'hui.
      Aucune affectation à la facture — un paiement ne fait que bouger le solde. */
  async function onPaid(movement: AccountMovement) {
    const amountEur = Math.abs(movement.amountEur);
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.payments.create(patientId, { amountEur, paidOn: today() });
      setNotice(`Paiement de ${formatEuros(amountEur)} enregistré.`);
      onMoved();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(movement: AccountMovement) {
    const label = `${formatEuros(movement.amountEur)} du ${formatDate(movement.date)}`;
    if (!window.confirm(`Supprimer le paiement de ${label} ?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.payments.remove(movement.id);
      onMoved();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel__head">
        <h2 className="panel__title">Compte</h2>
        {account.data && <SoldeChip soldeEur={account.data.soldeEur} />}
      </div>

      {account.error && <p className="error" role="alert">{account.error}</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {notice && <p className="muted">{notice}</p>}
      {account.loading && !account.data && <p className="muted">Chargement…</p>}

      {account.data && releve.length === 0 && (
        <p className="empty">Aucun mouvement : ni facture émise, ni paiement reçu.</p>
      )}
      {account.data && releve.length > 0 && open.length === 0 && !historyOpen && (
        <p className="empty">Rien en attente : les factures émises sont couvertes.</p>
      )}

      <ul className="releve">
        {shown.map((movement) => (
          <li className="releve__line" key={`${movement.kind}-${movement.id}`}>
            <span className="releve__label">
              {formatShortDate(movement.date)} · {movement.label}
              {movement.kind === 'note' && formatSent(movement) && (
                <span className="releve__sent">{formatSent(movement)}</span>
              )}
            </span>
            <span
              className={`releve__amount releve__amount--${movement.amountEur < 0 ? 'due' : 'credit'}`}
            >
              {formatSignedEuros(movement.amountEur)}
            </span>
            {movement.kind === 'note' && (
              <span className="releve__actions">
                <a className="button button--small" href={api.notes.pdfUrl(movement.id)} target="_blank" rel="noopener">
                  PDF
                </a>
                <button
                  type="button"
                  className="button button--small"
                  disabled={busy || !email.configured}
                  title={email.configured ? undefined : 'L’envoi par email n’est pas configuré (Réglages).'}
                  onClick={() => void onSend(movement)}
                >
                  {movement.sentAt ? 'Renvoyer' : 'Envoyer'}
                </button>
                <button
                  type="button"
                  className="button button--small button--primary"
                  disabled={busy}
                  title="Enregistrer un paiement du montant exact de cette facture, daté d’aujourd’hui"
                  onClick={() => void onPaid(movement)}
                >
                  Payé
                </button>
              </span>
            )}
            {movement.kind === 'payment' && (
              <button
                type="button"
                className="button button--small button--danger releve__remove"
                disabled={busy}
                onClick={() => void onRemove(movement)}
              >
                Supprimer
              </button>
            )}
          </li>
        ))}
      </ul>

      {history.length > 0 && (
        <button
          type="button"
          className="chiffres__toggle releve__history"
          aria-expanded={historyOpen}
          onClick={() => setHistoryOpen((v) => !v)}
        >
          <span>
            {historyOpen ? 'Masquer l’historique' : 'Historique'} · {history.length} mouvement
            {history.length > 1 ? 's' : ''}
          </span>
          <span className="chiffres__chevron" aria-hidden="true">
            {historyOpen ? '⌃' : '⌄'}
          </span>
        </button>
      )}

      <PaymentForm patientId={patientId} onAdded={onMoved} />
    </div>
  );
}

interface LocationsPanelProps {
  patient: Patient;
  pending: boolean;
  onChanged: () => void;
}

function LocationsPanel({ patient, pending, onChanged }: LocationsPanelProps) {
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const places = placesOf(patient);

  async function onDelete(location: Location) {
    if (!window.confirm(`Supprimer le lieu « ${location.label} » ?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.patients.removeLocation(patient.id, location.id);
      onChanged();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel__head">
        <h2 className="panel__title">Lieux de visite</h2>
        {editing !== 'new' && (
          <button type="button" className="button button--small" onClick={() => setEditing('new')}>
            Ajouter un lieu
          </button>
        )}
      </div>

      {error && <p className="error" role="alert">{error}</p>}

      {places.length === 0 && editing !== 'new' && (
        <p className="empty">
          Aucun lieu enregistré. Ajoutez-en un — domicile, école, cabinet — pour pouvoir planifier
          des séances.
        </p>
      )}

      <ul className="list">
        {places.map((location) =>
          editing === location.id ? (
            <li key={location.id}>
              <LocationForm
                patientId={patient.id}
                location={location}
                onDone={() => {
                  setEditing(null);
                  onChanged();
                }}
                onCancel={() => setEditing(null)}
              />
            </li>
          ) : (
            <li key={location.id} className="list__row">
              <span className="list__lead">{location.label}</span>
              <span className="muted">{location.address}</span>
              <span className="list__actions">
                <button
                  type="button"
                  className="button button--small"
                  disabled={pending || busy}
                  onClick={() => setEditing(location.id)}
                >
                  Modifier
                </button>
                <button
                  type="button"
                  className="button button--small button--danger"
                  disabled={pending || busy}
                  onClick={() => void onDelete(location)}
                >
                  Supprimer
                </button>
              </span>
            </li>
          ),
        )}
      </ul>

      {editing === 'new' && (
        <LocationForm
          patientId={patient.id}
          location={null}
          onDone={() => {
            setEditing(null);
            onChanged();
          }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

interface LocationFormProps {
  patientId: number;
  location: Location | null;
  onDone: () => void;
  onCancel: () => void;
}

function LocationForm({ patientId, location, onDone, onCancel }: LocationFormProps) {
  const [label, setLabel] = useState(location?.label ?? '');
  const [address, setAddress] = useState(location?.address ?? '');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    const body: CreateLocationRequest = { label: label.trim(), address: address.trim() };
    try {
      if (location) await api.patients.updateLocation(patientId, location.id, body);
      else await api.patients.addLocation(patientId, body);
      onDone();
    } catch (cause) {
      setError(errorMessage(cause));
      setPending(false);
    }
  }

  return (
    <form className="form form--inline" onSubmit={onSubmit}>
      <label className="field">
        <span className="field__label">Libellé</span>
        <input
          className="input"
          placeholder="Domicile, École Jean Moulin…"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          required
          autoFocus
        />
      </label>

      <label className="field">
        <span className="field__label">Adresse</span>
        <AddressInput
          placeholder="12 rue des Lilas, 31000 Toulouse"
          value={address}
          onChange={setAddress}
          required
        />
      </label>
      <LocationPreview patientId={patientId} address={address} />

      {error && <p className="error" role="alert">{error}</p>}
      <div className="form__actions">
        <button
          className="button button--primary"
          type="submit"
          disabled={pending || !label.trim() || !address.trim()}
        >
          {location ? 'Enregistrer' : 'Ajouter'}
        </button>
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={pending}>
          Annuler
        </button>
      </div>
    </form>
  );
}

/** Waits for her to stop typing before asking Google where that address is. */
const GEOCODE_DEBOUNCE_MS = 700;
const MIN_ADDRESS_LENGTH = 8;

/**
 * Where the new address sits relative to the rest of the tour: the pin Google
 * matched, plus a grey dot for every other patient's place. Silently absent
 * without a key, and never blocks the form.
 */
function LocationPreview({ patientId, address }: { patientId: number; address: string }) {
  const { key } = useMapsKey();
  const [point, setPoint] = useState<GeocodedPoint | null>(null);
  const others = useResource(() => api.patients.list(), []);
  const trimmed = address.trim();

  useEffect(() => {
    if (!key || trimmed.length < MIN_ADDRESS_LENGTH) {
      setPoint(null);
      return;
    }

    let active = true;
    const timer = setTimeout(() => {
      api.maps
        .geocode(trimmed)
        .then((response) => {
          if (active) setPoint(response.result);
        })
        .catch(() => {
          if (active) setPoint(null);
        });
    }, GEOCODE_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [key, trimmed]);

  const points = useMemo<MapPoint[]>(() => {
    if (!point) return [];
    const neighbours = (others.data ?? [])
      .filter((patient) => patient.id !== patientId)
      .flatMap((patient) => patient.locations)
      .flatMap((location) =>
        location.lat === null || location.lng === null
          ? []
          : [{ lat: location.lat, lng: location.lng, title: location.address, muted: true }],
      );

    return [{ lat: point.lat, lng: point.lng, label: '', title: point.formattedAddress }, ...neighbours];
  }, [point, others.data, patientId]);

  if (!key || !point) return null;

  return (
    <div className="field">
      <MapCanvas points={points} height="11rem" className="map--inline" title="Aperçu du lieu" />
      <span className="hint">{point.formattedAddress}</span>
    </div>
  );
}
